import { useEffect, useState } from 'react';

/**
 * ویزیت آنلاین «چت‌اول»: بیمار اول وارد گفتگو با پزشک می‌شود، بعد پرداخت می‌کند و بعد نوبت ثبت می‌شود.
 * پزشک تا پرداخت گفتگو را نمی‌بیند. روی حامی (hami) ساخته می‌شود و این‌جا فقط مسیر بیمار به آن‌جا است.
 *
 * رول‌اوت: فقط یک پروفایل. فهرست پزشک‌های مجاز سمت سرور حامی (OV_CHAT_DOCTORS) هم هست، یعنی دور زدن
 * این ثابت‌ها چیزی را باز نمی‌کند. همه‌چیز بعد از mount سمت کلاینت تصمیم گرفته می‌شود تا SSR و کش
 * صفحات دست نخورد (docs/performance.md).
 */
export const OV_CHAT_ROLLOUT_SLUGS: readonly string[] = ['دکتر-سیدعلیرضا-صدرالساداتی'];

/** false: فقط دستگاهی که یک بار با `?ovchat=1` باز کرده. true: همهٔ بازدیدکننده‌های همان پروفایل. */
export const OV_CHAT_OPEN_TO_ALL = false;

export const HAMI_ORIGIN = 'https://hami.paziresh24.com';
export const OV_REF_PARAM = 'ov_ref';
const OPT_IN_KEY = 'ov-chat-opt-in';

const decode = (slug: string) => {
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
};

export const isOvChatRolloutSlug = (slug?: string | null) => !!slug && OV_CHAT_ROLLOUT_SLUGS.includes(decode(slug));

export const buildOvStartUrl = (slug: string) => `${HAMI_ORIGIN}/chats/ov/start?slug=${encodeURIComponent(decode(slug))}`;
export const buildOvFinishUrl = (ref: string, bookId: string | number) =>
  `${HAMI_ORIGIN}/chats/ov/finish?ref=${encodeURIComponent(ref)}&book=${encodeURIComponent(String(bookId))}`;
export const buildOvFailedUrl = (ref: string) => `${HAMI_ORIGIN}/chats/ov/failed?ref=${encodeURIComponent(ref)}`;

/** کلید گفتگو از آدرس صفحهٔ فاکتور. فقط رشتهٔ کوتاه و بی‌خطر پذیرفته می‌شود. */
export const readOvRef = (value: unknown) => (typeof value === 'string' && /^ovpre:\d{1,12}:\d{1,12}$/.test(value) ? value : undefined);

/**
 * روی صفحهٔ فاکتور (مقصد همهٔ دکمه‌های «ویزیت آنلاین»): برای پروفایل رول‌اوت، بیمار به گفتگو می‌رود.
 * `?ovchat=1` همین دستگاه را وارد می‌کند و `?ovchat=0` خارج. فقط روی پروفایل رول‌اوت خوانده و نوشته می‌شود.
 */
export const useOvChatStartRedirect = (slug?: string | null, enabled = true) => {
  const [redirecting, setRedirecting] = useState(false);
  useEffect(() => {
    if (!enabled || !isOvChatRolloutSlug(slug)) return;
    // ov_ref را مستقیم از آدرس می‌خوانیم، نه از router: اگر router هنوز آماده نباشد، بیمارِ در حال پرداخت به گفتگو برمی‌گشت و حلقه می‌شد.
    if (readOvRef(new URLSearchParams(window.location.search).get(OV_REF_PARAM))) return;
    try {
      const param = new URLSearchParams(window.location.search).get('ovchat');
      if (param === '1') window.localStorage.setItem(OPT_IN_KEY, '1');
      if (param === '0') window.localStorage.removeItem(OPT_IN_KEY);
      const optedIn = window.localStorage.getItem(OPT_IN_KEY) === '1';
      if (!OV_CHAT_OPEN_TO_ALL && !optedIn) return;
    } catch {
      if (!OV_CHAT_OPEN_TO_ALL) return;
    }
    setRedirecting(true);
    window.location.replace(buildOvStartUrl(slug as string));
  }, [slug, enabled]);
  return redirecting;
};
